import { Button } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import * as clipboard from 'clipboard-polyfill';
import moment from 'moment';
import React, { useCallback, useContext, useState } from 'react';
import { SessionContext } from '../../context/SessionContext';
import { ToastContext } from '../../context/ToastContext';
import { useGetAnswer } from '../../hooks/chat/useGetAnwser';
import { useSendFeedback } from '../../hooks/chat/useSendFeedback';
import { useSendQuestion } from '../../hooks/chat/useSendQuestion';
import { useTrackUsage } from '../../hooks/usage/track';
import { Question } from '../../services/chat/getSessions.service';
import GenieAvatar from '../GenieAvatar';
import Icon from '../Icon';
import LoadingDots from '../LoadingDots/LoadingDots';
import UserAvatar from '../UserAvatar';
import Documents, { Document } from './Documents';
import styles from './Message.module.scss';

const cx = classNames.bind(styles);

interface MessageProps<TSender> {
  text: React.ReactNode;
  sender: TSender;
  date: string;
  documents?: Document[];
  hideActions?: boolean;
  sessionId?: never;
  historyItem?: never;
}

interface MessagePropsWithActions extends Omit<MessageProps<'genie'>, 'sessionId' | 'historyItem'> {
  sessionId: string;
  historyItem: Question;
}

const Message = <TSender extends 'genie' | 'user'>({
  text,
  sender,
  date,
  documents,
  hideActions,
  sessionId,
  historyItem,
}: MessageProps<TSender> | MessagePropsWithActions): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: resend } = useGetAnswer();
  const { send } = useSendQuestion();
  const { success } = useContext(ToastContext);
  const { mutate, isLoading: feedbackIsLoading } = useSendFeedback((positive) => {
    if (positive) {
      success('Thank you for your feedback');
    }
  });
  const { searchTerm } = useContext(SessionContext);
  const trackUsage = useTrackUsage();
  const formattedDate = moment.utc(date).local().format('hh:mm a');
  const feedbackIsDisabled = historyItem?.usr_thumbs_up !== undefined;

  const handleCopy = useCallback(() => {
    trackUsage.copyResponse({
      sessionId: sessionId ?? '',
      seqId: historyItem?.query_seq_id ?? -1,
    });
    const docs = documents?.map((doc) => `${doc.title}\nlink: ${doc.url}`) ?? [];
    clipboard.writeText(
      `${text}${
        docs.length > 0 ? `\n\nsources:\n\n${docs.join('\n\n')}` : ''
      }\n\nGenerated from CreditSights Genie as of ${moment.utc(date).local().format('DD MMM YYYY')}`
    );

    success('Copied to clipboard');
  }, [documents, text, date, trackUsage, sessionId, historyItem]);

  const handleResend = useCallback(async () => {
    if (sessionId && historyItem) {
      setIsLoading(true);
      await send({ query: historyItem.query, seqId: historyItem.query_seq_id });
      setIsLoading(false);
    }
  }, [sessionId, historyItem, resend]);

  const handleSendFeedback = (positive: boolean) => {
    if (sessionId && historyItem) {
      mutate({
        sessionId,
        seqId: historyItem.query_seq_id,
        user_feedback_type: 'local',
        comments: '',
        feedback_body: {
          user_thumbs_up: positive,
        },
      });
    }
  };

  if (sender === 'genie') {
    return (
      <div>
        <div className={cx('header')}>
          <div>
            <GenieAvatar />
          </div>
          <div className={cx('sender')}>Genie</div>
          <div>{formattedDate}</div>
        </div>
        {isLoading ? (
          <div className={cx('text')}>
            <LoadingDots />
          </div>
        ) : (
          <>
            <div className={cx('text')}>{text}</div>
            {searchTerm && typeof text === 'string' && text.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ? (
              <span data-scroll-point="1" />
            ) : null}
            {documents?.length ? (
              <div className={cx('documents')}>
                <Documents list={documents} />
              </div>
            ) : null}
            {hideActions ? null : (
              <div className={cx('actions')} data-csgenie-feedback-actions={feedbackIsDisabled ? 1 : 0}>
                <Button className={cx('actions-btn')} onClick={handleCopy}>
                  <Icon.Copy />
                </Button>
                <Button className={cx('actions-btn')} onClick={handleResend} disabled={feedbackIsLoading}>
                  <Icon.Reload />
                </Button>
                <Button
                  className={cx('actions-btn')}
                  onClick={() => handleSendFeedback(true)}
                  disabled={feedbackIsLoading || feedbackIsDisabled}
                >
                  <Icon.ThumbUp filled={historyItem?.usr_thumbs_up === true} />
                </Button>
                <Button
                  className={cx('actions-btn')}
                  onClick={() => handleSendFeedback(false)}
                  disabled={feedbackIsLoading || feedbackIsDisabled}
                >
                  <Icon.ThumbDown filled={historyItem?.usr_thumbs_up === false} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
  return (
    <div>
      <div className={cx('header')}>
        <div>
          <UserAvatar />
        </div>
        <div className={cx('sender')}>You</div>
        <div>{formattedDate}</div>
      </div>
      <div className={cx('text')}>{text}</div>
      {searchTerm && typeof text === 'string' && text.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ? (
        <span data-scroll-point="1" />
      ) : null}
    </div>
  );
};

export default Message;
